import { toast } from '@askable/ui/core/sonner';
import { useFeatureFlags } from 'feature-flags';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBrowserTimezone } from 'shared-utils';
import { useNavigate } from 'src/lib/routing';
import { match } from 'ts-pattern';
import { useMutation } from 'urql';

import { DEFAULT_LANGUAGE } from 'containers/Studies/Recruit/utils/defaultLanguage';
import { getDefaultRecruitLocations } from 'containers/Studies/Recruit/utils/getDefaultRecruitLocations';
import { CreateAIModeratedBooking } from 'containers/Studies/data/CreateAIModeratedBooking.mutation';
import { CreateSurveyBooking } from 'containers/Studies/data/CreateSurveyBooking.mutation';
import { CreateUnmoderatedBooking } from 'containers/Studies/data/CreateUnmoderatedBooking.mutation';
import { CreateVideoCallBooking } from 'containers/Studies/data/CreateVideoCallBooking.mutation';
import { UpdateBookingConfig } from 'containers/Studies/data/UpdateBookingConfig.mutation';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { BookingRecruitLanguageFluency } from 'src/constants/graphql-enums';

import type { AIModeratedBooking } from 'containers/Studies/types/ai-moderated';
import type { Device, Language, LanguageLocale } from 'containers/Studies/types/recruit';
import type { UnmoderatedBooking } from 'containers/Studies/types/unmoderated';
import { handleError } from 'utils/error_utils';

// We could use the BOOKING_TYPE enum here but the numbers values are less readable: UNMODERATED: 5, AI_MODERATED: 6, SURVEY: 7
type StudyType = 'AI_MODERATED' | 'UNMODERATED' | 'SURVEY' | 'VIDEO_CALL';

const AI_MODERATED_CONTEXTS = [
  ['companyContext', 'Company Context'],
  ['productContext', 'Product Context'],
  ['audienceContext', 'Audience Context'],
  ['interviewContext', 'Interview Context'],
] as const;

export const useCreateStudy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const viewer = useConnectedClient();
  const { agent_otto } = useFeatureFlags(['agent_otto']);

  const [, createAIModeratedBooking] = useMutation(CreateAIModeratedBooking);
  const [, createSurveyBooking] = useMutation(CreateSurveyBooking);
  const [, createUnmoderatedBooking] = useMutation(CreateUnmoderatedBooking);
  const [, createVideoCallBooking] = useMutation(CreateVideoCallBooking);
  const [, updateBookingConfig] = useMutation(UpdateBookingConfig);

  const [isCreating, setIsCreating] = useState(false);

  const getDefaultParticipants = (studyType: StudyType) =>
    match(studyType)
      .with('AI_MODERATED', () => 15)
      .with('UNMODERATED', () => 15)
      .with('SURVEY', () => 50)
      .with('VIDEO_CALL', () => 5)
      .exhaustive();

  const getDefaultRecruitConfig = (studyType: StudyType) => ({
    participants: getDefaultParticipants(studyType),
    ...(studyType === 'VIDEO_CALL' ? { device: 'any_device' as const } : {}),
    locations: getDefaultRecruitLocations({
      billingCountry: viewer.details?.ConnectedTeam?.settings?.billing?.location?.country,
      profileCountry: viewer.details?.location?.country,
    }),
    age_range: [18, 99] as [number, number],
    participant_source: 'panel' as const,
    participants_excluded_months: 3,
    custom_terms: null,
    screener_questions: [],
    industry_and_occupation: [],
    languages: [DEFAULT_LANGUAGE],
    education_level: [],
    marital_status: [],
    occupational_status: [],
    business_ownership: [],
  });

  const updateBookingWithDefaults = async (bookingId: string, studyType: StudyType, languages?: Language[]) => {
    const result = await updateBookingConfig({
      _id: bookingId,
      input: {
        recruit: { ...getDefaultRecruitConfig(studyType), ...(languages ? { languages } : {}) },
        contact: {
          _primary_contact_id: viewer.details?._id ?? null,
        },
        listing_information: {
          title: '',
          description:
            studyType === 'AI_MODERATED'
              ? t('sections.studies.recruit.fields.descriptionPlaceholderAiMod')
              : t('sections.studies.recruit.fields.descriptionPlaceholder'),
        },
      },
    });

    if (result.error) {
      toast.error(result.error?.graphQLErrors[0].message ?? 'Error creating study');
      return false;
    }

    return true;
  };

  const createStudy = async ({
    studyType,
    studyName,
    device,
    interviewLanguages,
    context,
    researchGoals,
    projectId,
  }: {
    studyType: StudyType;
    studyName?: string;
    device?: Device;
    interviewLanguages?: LanguageLocale[];
    context?: {
      companyContext?: string;
      productContext?: string;
      audienceContext?: string;
      interviewContext?: string;
    };
    researchGoals?: string[];
    projectId?: string;
  }) => {
    try {
      setIsCreating(true);
      const languages = interviewLanguages?.map(locale => ({
        locale,
        fluency: [BookingRecruitLanguageFluency.Fluent, BookingRecruitLanguageFluency.Native],
      }));

      const { result, bookingId } = await match(studyType)
        .with('AI_MODERATED', async () => {
          const result = await createAIModeratedBooking({
            input: {
              name: studyName ?? t('sections.studies.newStudy'),
              _project_id: projectId,
              config: {
                ai_moderated: {
                  agent_name: agent_otto ? 'OTTO' : 'NEIL',
                  intro_block: {
                    title: t('sections.studies.build.blocks.intro.defaultTitle'),
                    instructions: t('sections.studies.build.blocks.intro.defaultDescription'),
                  },
                  outro_block: {
                    title: t('sections.studies.build.blocks.outro.defaultTitle'),
                    instructions: t('sections.studies.build.blocks.outro.defaultDescription'),
                  },
                  contexts: AI_MODERATED_CONTEXTS.flatMap(([key, title]) => {
                    const body = context?.[key];
                    return body ? [{ title, body, type: 'text' as const }] : [];
                  }),
                  task_blocks: (researchGoals || []).map(goal => ({
                    title: '',
                    research_goal: {
                      goal,
                    },
                  })),
                },
                recruit: {
                  device,
                  languages,
                },
              },
            },
          });
          return {
            result,
            bookingId: (result.data as { createAIModeratedBooking?: Pick<AIModeratedBooking, '_id'> })
              ?.createAIModeratedBooking?._id,
          };
        })
        .with('UNMODERATED', async () => {
          const result = await createUnmoderatedBooking({
            input: {
              name: t('sections.studies.newStudy'),
              ...(projectId ? { _project_id: projectId } : {}),
              config: {
                unmoderated: {
                  welcome_block: {
                    title: t('sections.studies.build.blocks.welcome.defaultTitle'),
                    instructions: t('sections.studies.build.blocks.welcome.defaultDescription'),
                  },
                  thank_you_block: {
                    title: t('sections.studies.build.blocks.thank_you.defaultTitle'),
                    instructions: t('sections.studies.build.blocks.thank_you.defaultDescription'),
                  },
                },
              },
            },
          });
          return {
            result,
            bookingId: (result.data as { createUnmoderatedBooking?: Pick<UnmoderatedBooking, '_id'> })
              ?.createUnmoderatedBooking?._id,
          };
        })
        .with('SURVEY', async () => {
          const result = await createSurveyBooking({
            input: {
              name: t('sections.studies.newStudy'),
              ...(projectId ? { _project_id: projectId } : {}),
              config: {
                survey: {
                  welcome_block: {
                    title: t('sections.studies.build.blocks.welcome.defaultTitle'),
                    instructions: t('sections.studies.build.blocks.welcome.defaultDescription'),
                  },
                  thank_you_block: {
                    title: t('sections.studies.build.blocks.thank_you.defaultTitle'),
                    instructions: t('sections.studies.build.blocks.thank_you.defaultDescription'),
                  },
                },
              },
            },
          });
          return {
            result,
            bookingId: result.data?.createSurveyBooking?._id,
          };
        })
        .with('VIDEO_CALL', async () => {
          const result = await createVideoCallBooking({
            input: {
              name: t('sections.studies.newStudy'),
              ...(projectId ? { _project_id: projectId } : {}),
              timezone: getBrowserTimezone(),
              is_self_scheduling_enabled: true,
            },
          });
          return { result, bookingId: result.data?.createVideoCallBooking?._id };
        })
        .exhaustive();

      if (result.error || !bookingId) {
        toast.error(result.error?.graphQLErrors[0].message ?? 'Error creating study');
        return;
      }

      if (!(await updateBookingWithDefaults(bookingId, studyType, languages))) {
        return;
      }

      navigate(studyType === 'VIDEO_CALL' ? `/studies/${bookingId}/schedule` : `/studies/${bookingId}/build`);
    } catch (e) {
      const errorMessage = handleError(e, 'Error creating study');
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateAIModerated = ({
    studyName,
    device,
    interviewLanguages,
    context,
    researchGoals,
    projectId,
  }: {
    studyName: string;
    device: Device;
    interviewLanguages: LanguageLocale[];
    context?: {
      companyContext?: string;
      productContext?: string;
      audienceContext?: string;
      interviewContext?: string;
    };
    researchGoals?: string[];
    projectId?: string;
  }) => {
    return createStudy({
      studyType: 'AI_MODERATED',
      studyName,
      device,
      interviewLanguages,
      context,
      researchGoals,
      projectId,
    });
  };

  const handleCreateUnmoderated = (projectId?: string) => createStudy({ studyType: 'UNMODERATED', projectId });

  const handleCreateSurvey = (projectId?: string) => createStudy({ studyType: 'SURVEY', projectId });

  const handleCreateVideoCall = (projectId?: string) => createStudy({ studyType: 'VIDEO_CALL', projectId });

  return {
    handleCreateAIModerated,
    handleCreateUnmoderated,
    handleCreateSurvey,
    handleCreateVideoCall,
    isCreating,
  };
};
